import { SPHERE_IDS } from './spheres.js';
import { balance } from './balance.js';
import { DEFAULT_LOCALE } from './locale.js';
import { domainCopy } from './i18n/index.js';
import { isDone } from './done.js';
import { FIRST_MONTH_DAYS, usageStart } from './goals.js';
/**
 * отчет за месяц ведения матрицы (30 дней от первой отметки). правки Кати:
 * увидеть закономерности, как менялась удовлетворенность сферами и как на
 * это повлияли навыки: оставить их или заменить; в конце второго месяца —
 * новые практики.
 */
export const PERIOD_DAYS = FIRST_MONTH_DAYS;
function addDays(day, n) {
    const d = new Date(`${day}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}
/** сколько 30-дневных периодов уже закончилось к сегодняшнему дню */
export function completedPeriods(state, today) {
    const start = usageStart(state);
    if (!start)
        return 0;
    const days = Math.floor((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000);
    return Math.max(0, Math.floor(days / PERIOD_DAYS));
}
/** первый день периода n (с 1) */
export function periodStart(state, n) {
    const start = usageStart(state);
    return start ? addDays(start, (n - 1) * PERIOD_DAYS) : null;
}
const HIGH = 0.5;
const LOW = 0.2;
export function periodReport(state, n) {
    const from = periodStart(state, n);
    if (!from)
        return null;
    const to = addDays(from, PERIOD_DAYS - 1);
    const days = Array.from({ length: PERIOD_DAYS }, (_, i) => addDays(from, i));
    let ones = 0;
    const filledSet = new Set();
    const perSkill = state.skills.map((k) => {
        const n1 = days.filter((d) => isDone(k, d)).length;
        ones += n1;
        for (const d of days)
            if (isDone(k, d))
                filledSet.add(d);
        return { k, ones: n1 };
    });
    let bestStreak = 0;
    let run = 0;
    for (const d of days) {
        run = filledSet.has(d) ? run + 1 : 0;
        bestStreak = Math.max(bestStreak, run);
    }
    const fixed = state.history.filter((h) => h.date >= from && h.date <= to).sort((a, b) => (a.date < b.date ? -1 : 1));
    const first = fixed[0];
    const last = fixed.at(-1);
    const twoPoints = first && last && first.date !== last.date;
    const spheres = SPHERE_IDS.map((id) => {
        const mine = perSkill.filter((x) => x.k.sphere === id);
        const activity = mine.length ? mine.reduce((a, x) => a + x.ones, 0) / (mine.length * PERIOD_DAYS) : 0;
        const valueFrom = twoPoints ? (first.values[id] ?? null) : null;
        const valueTo = twoPoints ? (last.values[id] ?? null) : null;
        let verdict = 'steady';
        if (valueFrom === null || valueTo === null)
            verdict = 'unknown';
        else {
            const delta = valueTo - valueFrom;
            if (mine.length && activity >= HIGH)
                verdict = delta >= 1 ? 'keep' : 'replace';
            else if (activity <= LOW)
                verdict = delta <= -1 ? 'neglected' : delta >= 1 ? 'grows' : 'steady';
        }
        return { id, skills: mine.length, activity, valueFrom, valueTo, verdict };
    });
    const sorted = [...perSkill].sort((a, b) => b.ones - a.ones);
    const withGoal = state.skills.filter((k) => k.goal);
    return {
        n,
        from,
        to,
        days: PERIOD_DAYS,
        filled: filledSet.size,
        ones,
        bestStreak,
        balanceFrom: twoPoints ? balance(first.values) : null,
        balanceTo: twoPoints ? balance(last.values) : null,
        spheres,
        top: sorted.filter((x) => x.ones > 0).slice(0, 3).map((x) => ({ name: x.k.name, ones: x.ones })),
        // «почти не делались»: меньше трех раз за месяц
        idle: sorted.filter((x) => x.ones < 3).map((x) => x.k.name),
        goals: n >= 2 && withGoal.length
            ? { set: withGoal.length, reached: perSkill.filter((x) => x.k.goal && x.ones >= x.k.goal).length }
            : null,
        suggestPractices: n === 2,
    };
}
/**
 * отчет словами: абзацы строчными (стиль приложения). бот делает первую
 * букву заглавной. практики — названия сфер, для которых они есть в озере
 */
export function reportParagraphs(r, nm, lakeSpheres = [], locale = DEFAULT_LOCALE) {
    const t = domainCopy(locale).report;
    const out = [];
    const pct = Math.round((r.filled / r.days) * 100);
    out.push(t.filled({ filled: r.filled, days: r.days, pct, ones: r.ones, streak: r.bestStreak }));
    if (r.balanceFrom !== null && r.balanceTo !== null)
        out.push(t.balance({ from: r.balanceFrom, to: r.balanceTo }));
    else
        out.push(t.noBalance);
    const by = (v) => r.spheres.filter((s) => s.verdict === v).map((s) => ({ name: nm(s.id), from: s.valueFrom, to: s.valueTo }));
    if (by('keep').length)
        out.push(t.keep({ list: by('keep') }));
    if (by('replace').length)
        out.push(t.replace({ list: by('replace') }));
    if (by('neglected').length)
        out.push(t.neglected({ list: by('neglected') }));
    if (by('grows').length)
        out.push(t.grows({ list: by('grows') }));
    const noSkills = r.spheres.filter((s) => s.skills === 0).map((s) => nm(s.id));
    if (noSkills.length)
        out.push(t.noSkills({ names: noSkills }));
    if (r.top.length)
        out.push(t.top({ items: r.top }));
    if (r.idle.length)
        out.push(t.idle({ names: r.idle.slice(0, 6), more: Math.max(0, r.idle.length - 6) }));
    if (r.goals)
        out.push(t.goals({ reached: r.goals.reached, set: r.goals.set }));
    if (r.suggestPractices) {
        const weak = [...r.spheres].filter((s) => s.valueTo !== null).sort((a, b) => (a.valueTo ?? 0) - (b.valueTo ?? 0))[0];
        out.push(t.practices({ lake: lakeSpheres.map(nm), weak: weak ? nm(weak.id) : null }));
    }
    return out;
}
//# sourceMappingURL=report.js.map